import { Box, Stack } from '@mui/material'
import { morphForWords } from 'components/shared/processes'
import moment from 'moment'
import ReactApexChart from 'react-apexcharts'
import { COLORS } from 'styles/colors'
import ChartLegend from 'ui/charts/legends/ChartLegend'
import { TypographyPrimary14Bold } from 'ui/typography/TypographyPrimary14Bold'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'

import { localizationApexCharts } from 'utils'

interface FeedConsumptionChartProps {
	timestamp: number[]
	distributedFood: number[] | number
	consumptionFood: number[] | number
	height?: number
}

const FeedConsumptionChart = (props: FeedConsumptionChartProps) => {
	const { timestamp, distributedFood, consumptionFood, height } = props
	const validDistributedFood = Array.isArray(distributedFood)
		? distributedFood
		: typeof distributedFood === 'number'
			? [distributedFood]
			: []

	const validConsumptionFood = Array.isArray(consumptionFood)
		? consumptionFood
		: typeof consumptionFood === 'number'
			? [consumptionFood]
			: []

	const isEmptyData =
		validDistributedFood.length === 0 && validConsumptionFood.length === 0
	const series: any = [
		{
			name: 'Расход корма',
			data: validDistributedFood,
		},
		{
			name: 'Остаток',
			data: validConsumptionFood,
		},
	]

	const options: any = {
		mask: false,
		colors: [COLORS.main, COLORS.error],
		chart: {
			locales: localizationApexCharts(),
			defaultLocale: 'ru',
			zoom: {
				enabled: false,
			},
			height: 250,
			type: 'line',
			toolbar: {
				show: false,
			},
		},
		markers: {
			size: 0,
			colors: ['#fff'],
			strokeColors: [COLORS.main, COLORS.error],
			strokeWidth: 2,
			hover: {
				size: 5,
			},
		},
		stroke: {
			width: 2,
			curve: 'straight',
		},
		title: {
			style: {
				fontSize: '12px',
				fontFamily: 'Montserrat',
				fontStyle: 'normal',
				fontWeight: 400,
				lineHeight: '15px',
				color: '#7F7F84',
			},
		},

		dataLabels: {
			enabled: false,
		},
		grid: {
			strokeDashArray: [6, 3],
			show: true,
			xaxis: {
				lines: {
					show: true,
				},
			},
		},
		xaxis: {
			crosshairs: {
				show: true,
				opacity: 1,
				position: 'back',
				stroke: {
					width: 1,
					dashArray: 0,
				},
			},
			type: 'datetime',
			tooltip: {
				enabled: false,
			},
			labels: {
				datetimeUTC: false,
				style: {
					fontSize: '12px',
					fontFamily: 'Montserrat',
					fontStyle: 'normal',
					fontWeight: 400,
					lineHeight: '15px',
					color: '#7F7F84',
				},
				datetimeFormatter: {
					year: 'yyyy',
					month: 'MM',
					day: 'dd.MM',
					hour: 'HH:mm',
				},
				formatter: (val: number) => moment(val).format('DD.MM'),
				rotate: 0,
			},
			categories: timestamp,
		},
		yaxis: {
			tickAmount: 4,
			min: 0,
			max: Math.round(
				Math.max(...validDistributedFood, ...validConsumptionFood) * 1.15,
			),
			labels: {
				style: {
					fontSize: '12px',
					fontFamily: 'Montserrat',
					fontStyle: 'normal',
					fontWeight: 400,
					lineHeight: '15px',
					color: '#7F7F84',
				},
				formatter: function (value: number, index: number) {
					if (index % 2 !== 0) {
						return ''
					}
					return Math.round(value).toString()
				},
			},
		},
		tooltip: {
			fixed: true,
			custom: ({ series, seriesIndex, dataPointIndex, w }) => {
				const timeValue = moment(
					w.config.xaxis.categories[dataPointIndex],
				).format('D.MM.YY')
				const perfectPoint = validDistributedFood[dataPointIndex]

				const criticalPoint = validConsumptionFood[dataPointIndex]
				return `<div
				style='width:max-content; height:auto; background-color: #fff; border-radius:12px; border: hidden; box-shadow: 0px 0px 16px 0px rgba(4, 6, 6, 0.16); position: relative'>
				<div style='padding:16px 8px; border: none;'>
					<div style='display: flex; flex-direction: column; gap:4px'>

						<div style='display:flex; flex-direction:row; align-items: center; gap:4px'>
							<span style='width:12px; height:12px; border-radius: 50%; background-color: ${
								COLORS.main
							};'></span>
							<p style='font-weight: 600; font-size: 16px; color: #000; margin: 0px'>${perfectPoint}</p>
							<p style='font-weight: 400; font-size: 12px; color: #7F7F84; margin: 0px'>${morphForWords(
								perfectPoint,
								['голова', 'головы', 'голов'],
							)}</p>
						</div>

						<div style='display:flex; flex-direction:row; align-items: center; gap:4px'>
						<span style='width:12px; height:12px; border-radius: 50%; background-color: ${
							COLORS.error
						};'></span>
						<p style='font-weight: 600; font-size: 16px; color: #000; margin: 0px'>${criticalPoint}</p>
						<p style='font-weight: 400; font-size: 12px; color: #7F7F84; margin: 0px'>${morphForWords(
							criticalPoint,
							['голова', 'головы', 'голов'],
						)}</p>
					</div>


						<div>
							<p style='font-weight: 400; font-size: 12px; color: #7F7F84; margin: 0px'>${timeValue}</p>
						</div>
					</div>
				</div>
			</div>`
			},
		},
		legend: {
			show: false,
		},
	}
	const descriptionForLegend = [
		{ color: COLORS.main, description: 'Расход корма' },

		{ color: COLORS.error, description: 'Остаток' },
	]
	return (
		<Box
			sx={{
				width: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			{isEmptyData ? (
				<Stack
					direction={'column'}
					justifyContent={'center'}
					alignItems={'center'}
					gap={'4px'}
				>
					<TypographyPrimary14Bold sx={{ color: COLORS.icon }}>
						Нет данных
					</TypographyPrimary14Bold>
					<TypographySecondary14>
						Нет данных, соответствующих заданным условиям
					</TypographySecondary14>
				</Stack>
			) : (
				<>
					<Stack direction='row'>
						<TypographySecondary>кг</TypographySecondary>
					</Stack>
					<ReactApexChart
						id={'feedLineConsumption'}
						options={{
							...options,
							chart: { ...options.chart, height: height ?? 146 },
						}}
						series={series}
						type='line'
						height={height ?? 146}
						width='100%'
					/>
					<ChartLegend initialLegends={descriptionForLegend} />
				</>
			)}
		</Box>
	)
}

export default FeedConsumptionChart
